// src/pages/group/hooks/useGroupScreen.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useItemInteractions } from "../../common/hooks/useItemInteractions";
import { Unit, ValueType, AvailabilityStatus } from "../../common/types/enums";
import {
  GroupedProduct,
  ItemAttribute,
  StockStore,
} from "../../common/types/types";
import {
  addToCart,
  decreaseQuantity,
  increaseQuantity,
  removeFromCart,
} from "../../store/slices/cartSlice";
import { selectPurchasedItemsMap } from "../../store/slices/orderSlice";
import {
  selectSaleItem,
  selectSaleItemList,
  upsertItems,
} from "../../store/slices/saleItemListSlice";
import { RootState } from "../../store/store";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { selectCategoryList } from "../../store/slices/categoryListSlice";
import { unitTypeToShort } from "../../common/utils/functions/enumToText";
import { groupProducts } from "../../common/utils/functions/groupProducts";
import { selectForceStores } from "../../store/slices/storeListSlice";
import { checkItemAvailability } from "../../common/utils/functions/checkItemAvailability";
import { useDisplayPrice } from "../../common/hooks/useDisplayPrice";
import { getApi } from "../../api/api";

const getAttributeKey = (attr: ItemAttribute): string =>
  `${attr.name}|${attr.fieldType}|${attr.valueType}|${attr.unit}`;

const parseValue = (
  value: string | string[] | boolean,
  valueType: ValueType,
): string | boolean | string[] => {
  if (valueType === ValueType.NUMBER) {
    const num = parseFloat(value as string);
    return isNaN(num) ? String(value) : num.toString();
  }
  if (valueType === ValueType.BOOLEAN) return Boolean(value);
  if (
    valueType === ValueType.STRING &&
    typeof value === "string" &&
    value.includes(",")
  ) {
    return value.split(",").map((v) => v.trim());
  }
  return value as string;
};

export const useGroupScreen = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { trackView, toggleFavorite } = useItemInteractions();
  const currentCluster = useSelector(selectCurrentCluster);
  const categories = useSelector(selectCategoryList);
  const allItems = useSelector(selectSaleItemList);
  const purchasedMap = useSelector(selectPurchasedItemsMap);
  const selectedStoreIds = useSelector(selectForceStores);
  const [isLoading, setIsLoading] = useState(false);
  const getPrice = useDisplayPrice();

  useEffect(() => {
    if (!id || allItems.length > 0) return;

    const loadGroup = async () => {
      setIsLoading(true);
      try {
        const api = getApi();
        const response = await api.getGroupItems(Number(id));
        if (response?.length) {
          dispatch(upsertItems(response));
        }
      } catch (error) {
        console.error("Failed to load group:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadGroup();
  }, [id, allItems.length, dispatch]);

  const group = useMemo(() => {
    if (!id || !allItems.length) return null;

    const groups = groupProducts(allItems);
    return groups.find(
      (item): item is GroupedProduct =>
        "type" in item &&
        item.type === "group" &&
        String(item.groupId) === String(id),
    );
  }, [allItems, id]);

  const getCategoryName = useCallback(
    (id: number) => {
      return categories.find((c) => c.id === id)?.name;
    },
    [categories],
  );

  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string | boolean | string[]>
  >({});

  useEffect(() => {
    if (!group?.selectors) return;

    const defaultVariant = group.variants?.[0];
    if (!defaultVariant) return;

    const initial: Record<string, string | boolean | string[]> = {};

    Object.keys(group.selectors).forEach((selectorKey: string) => {
      const attr = defaultVariant.attributes?.find(
        (a: ItemAttribute) => getAttributeKey(a) === selectorKey,
      );
      if (attr) initial[selectorKey] = parseValue(attr.value, attr.valueType);
    });

    setSelectedOptions(initial);
  }, [group]);

  const currentVariant = useMemo(() => {
    if (!group?.variants?.length) return null;

    const exactMatch = group.variants.find((variant) => {
      return Object.entries(selectedOptions).every(
        ([selectorKey, selectedValue]) => {
          const attr = variant.attributes?.find(
            (a) => getAttributeKey(a) === selectorKey,
          );
          if (!attr) return false;
          return parseValue(attr.value, attr.valueType) === selectedValue;
        },
      );
    });
    if (exactMatch) return exactMatch;

    let bestMatch = group.variants[0];
    let bestScore = 0;

    for (const variant of group.variants) {
      let score = 0;
      for (const [selectorKey, selectedValue] of Object.entries(
        selectedOptions,
      )) {
        const attr = variant.attributes?.find(
          (a) => getAttributeKey(a) === selectorKey,
        );
        if (attr && parseValue(attr.value, attr.valueType) === selectedValue) {
          score++;
        }
      }
      if (score > bestScore) {
        bestScore = score;
        bestMatch = variant;
      }
    }

    return bestMatch;
  }, [group?.variants, selectedOptions]);

  const currentImages = useMemo(
    () => currentVariant?.files || [],
    [currentVariant],
  );

  const currentPrice = useMemo(() => {
    if (!currentVariant) return 0;
    return getPrice(currentVariant.prices);
  }, [currentVariant, getPrice]);

  const currentItem = useSelector((state: RootState) =>
    selectSaleItem(state, currentVariant?.id!),
  );

  const cartItem = useSelector((state: RootState) => {
    if (!group?.id) return null;

    const itemsInOurGroup = state.cart.items.filter(
      (item) => item.groupId === String(group.id),
    );
    const itemInOurGroup = itemsInOurGroup.find(
      (item) =>
        JSON.stringify(item.selectedOptions) ===
        JSON.stringify(selectedOptions),
    );
    if (itemInOurGroup) return itemInOurGroup;
    return state.cart.items.find(
      (item) => !item.groupId && item.id === String(currentVariant?.id),
    );
  });

  const quantity = cartItem?.quantity || 0;



  const maxAvailable = useMemo(() => {
    const variant = currentVariant || group?.variants?.[0];
    if (!variant?.useStock) return Infinity;
    return (
      variant.stock_stores?.reduce(
        (s: number, i: StockStore) => s + (i.quantity - i.reserved),
        0,
      ) || 0
    );
  }, [currentVariant, group?.variants]);

  const isIncreaseDisabled = useMemo(() => {
    if (!currentVariant?.useStock) return false;
    return quantity >= maxAvailable;
  }, [quantity, maxAvailable, currentVariant?.useStock]);

  const availability = useMemo(() => {
    if (!currentVariant) {
      return { available: false, status: AvailabilityStatus.NO_STORE_SELECTED };
    }
    return checkItemAvailability({
      item: currentVariant,
      forceStore: currentCluster?.forceStore ?? false,
      selectedStoreIds,
    });
  }, [currentVariant, currentCluster?.forceStore, selectedStoreIds]);

  const isOutOfStock = !availability.available;

  const handleOptionSelect = useCallback(
    (selectorKey: string, value: string | boolean | string[]) => {
      setSelectedOptions((prev) => ({ ...prev, [selectorKey]: value }));
    },
    [],
  );

  const handleAddToCart = useCallback(() => {
    if (!currentVariant || !group?.id) return;
    if (!availability.available) return;
    dispatch(
      addToCart({
        id: String(currentVariant.id),
        name: currentVariant.name,
        price: currentPrice,
        image: currentVariant.files?.[0] || "",
        groupId: String(group.id),
        selectedOptions: { ...selectedOptions },
        categoryId: Number(currentVariant.categoryId),
        minQuantity: currentVariant.minQuantity ?? 1,
        quantityStep: currentVariant.quantityStep ?? 1,
        attributes: currentVariant.attributes || [],
              unit: currentVariant.unit, 
      }),
    );
  }, [
    dispatch,
    currentVariant,
    currentPrice,
    selectedOptions,
    group?.id,
    availability.available,
  ]);

  const handleRemove = useCallback(() => {
    if (cartItem) dispatch(removeFromCart(cartItem.id));
  }, [dispatch, cartItem]);

  const handleIncrease = useCallback(() => {
    if (!isIncreaseDisabled && cartItem && availability.available) {
      dispatch(increaseQuantity(cartItem.id));
    }
  }, [dispatch, cartItem, isIncreaseDisabled, availability.available]);

  const handleDecrease = useCallback(() => {
    if (cartItem) dispatch(decreaseQuantity(cartItem.id));
  }, [dispatch, cartItem]);

  const getUnitName = useCallback(
    (unit: Unit): string => unitTypeToShort(unit) || "",
    [],
  );

  const handleFavoritePress = async () => {
    if (!currentVariant?.selected) return;
    toggleFavorite(Number(currentVariant.id));
  };

  const hasPurchasedVariant = currentVariant
    ? purchasedMap.get(currentVariant.id!) || false
    : false;
  const canRateVariant =
    hasPurchasedVariant && currentVariant?.ratings?.length === 0;

  useEffect(() => {
    if (currentVariant?.id) {
      trackView(currentVariant.id);
    }
  }, [currentVariant?.id, trackView]);

  return {
    group,
    currentVariant,
    currentPrice,
    selectedOptions,
    quantity,
    getUnitName,
    handleOptionSelect,
    handleAddToCart,
    handleRemove,
    handleIncrease,
    handleDecrease,
    currentImages,
    handleFavoritePress,
    canRateVariant,
    currentItem,
    isOutOfStock,
    getCategoryName,
    maxAvailable,
    isIncreaseDisabled,
    availability,
    isLoading,
  };
};
